import { BookOpenIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import WizardForm from './WizardForm';
import { addCustomModel, updateCustomModel } from '../../api/registry';
import { registryActions } from '@/stores/registryStore';
import PropTypes from 'prop-types';

const WizardWrapper = ({
  title = 'Create a Model',
  subtitle,
  guideUrl,
  defaultValues,
  typeOptions,
  typeToUnitsMap,
  isLoading,
  warnings = [],
  steps,
  onSaved,
}) => {
  const [status, setStatus] = useState({
    isSubmitting: false,
  });

  const onSubmit = async formData => {
    try {
      setStatus(prev => ({ ...prev, isSubmitting: true }));

      const inputs = formData.inputs.map(input => ({
        ...input,
        unit: JSON.parse(input.unit),
      }));

      const outputs = formData.outputs.map(output => ({
        ...output,
        unit: JSON.parse(output.unit),
      }));

      const processedData = {
        permissions: formData.permissions,
      };

      if (formData.permissions === 'project') {
        processedData.project_ids = formData.project_ids;
      }

      const tea = {
        tea_type: formData.tea_type,
      };

      if (formData.tea_type === 'simple') {
        tea.data = {
          spec_type: formData.simpleCosts.spec_type,
          levelized_costs: formData.simpleCosts.levelized_costs.map(item => ({
            cost: { scalar: item.cost.scalar, unit: JSON.parse(item.cost.unit) },
            product: item.product,
          })),
        };
      } else {
        tea.data = {
          ...formData.bottomUpCosts,
          reference_capacity: {
            scalar: formData.bottomUpCosts.reference_capacity,
            unit: JSON.parse(formData.bottomUpCosts.conversions.reference_capacity),
          },
        };

        delete tea.data.conversions;
        delete tea.data.add_scheduled_replacement;
        delete tea.data.exponential_cost_scaling;
      }

      const params = formData.params?.map(param => ({
        path: param.path,
        value: parseFloat(param.baseline) || 0,
        tooltip: param.aspenTooltip,
        min: parseFloat(param.minimum) || 0,
        max: parseFloat(param.maximum) || 0,
      }));

      const payload = {
        ...processedData,
        data: {
          inputs,
          outputs,
          tea,
          // The API rejects a blank description but accepts none.
          description: formData.description?.trim() || undefined,
          label: formData.label,
          params: params?.length ? params : [],
        },
      };

      if (!formData.id) {
        payload.model_type = formData.model_type || 'ratio';
      }

      const { data } = formData.id ? await updateCustomModel(formData.id, payload) : await addCustomModel(payload);

      if (data) {
        await registryActions.refresh();
        // Land the user on the new module in the palette, as the equation
        // builder does, rather than on a success message.
        registryActions.highlightCustomModule(data.id);
        onSaved();
      }
    } finally {
      setStatus(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const { isSubmitting } = status;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="shrink-0 px-8 pt-5 pb-4 pr-16 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between gap-4">
          <h3 className="font-bold text-lg">{title}</h3>
          {guideUrl && (
            <a
              href={guideUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-xs btn-ghost gap-1"
              title="Open the documentation for this builder"
            >
              <BookOpenIcon className="h-3 w-3" />
              Guide
            </a>
          )}
        </div>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-10">
          <div className="loading loading-spinner loading-lg"></div>
        </div>
      ) : (
        <WizardForm
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          typeOptions={typeOptions}
          typeToUnitsMap={typeToUnitsMap}
          warnings={warnings}
          steps={steps}
        />
      )}
    </div>
  );
};

WizardWrapper.propTypes = {
  defaultValues: PropTypes.object.isRequired,
  typeOptions: PropTypes.array.isRequired,
  typeToUnitsMap: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  guideUrl: PropTypes.string,
  warnings: PropTypes.arrayOf(PropTypes.string),
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      Component: PropTypes.elementType.isRequired,
      componentProps: PropTypes.object,
    }),
  ).isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default WizardWrapper;
